import React, { useEffect, useState } from 'react'

import { useMediaQuery, useTheme } from '@mui/material'

import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'

import { useSavedDashboardFilterActions } from 'redux/actions/SavedDashboardFilters'

import Box from 'components_new/atoms/Box'

import PeriodSelector from './PeriodSelector'

import EditUserFiltersDialog from './dialogs/EditUserFilterDialog'

import Loading from './loading'
import { formatResourceFilterOptions } from 'utils/functions'

import * as UserEvents from 'redux/api/internal/UserEvents'
import {
  useSavedDashboardFilters,
  useShouldFetchSavedDashboardFilters
} from 'redux/hooks/SavedDashboardFilters'

import { Action, Context } from 'types/GlobalUserEvents'
import DashboardFilterSection from './DashboardFilterSection'

import {
  useDashboardFilter,
  usePeriodFilter,
  useSavedDashboardFilter
} from 'redux/hooks/DashboardPage'
import {
  useSetDashboardFilter,
  useSetDashboardPeriodFilter,
  useSetSavedDashboardFilter
} from 'helpers/hooks/Dashboards'
import { TDashboardGroup, TResourceFilter } from 'types/GlobalDashboardGroup'
import { isPublicDashboard, TDashboard } from 'types/GlobalDashboard'
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'
import { useGlobalFilterSaveKey } from 'redux/hooks/GlobalFilter'
import { useResourceFilterActions } from 'redux/actions/ResourceFilter'
import {
  useResourceFilterData,
  useResourceFilterOptions,
  useResourceFilterStatus
} from 'redux/hooks/ResourceFilter'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'

interface DashboardUserFilterProps {
  dashboard: TDashboard | null
  dashboardGroup: TDashboardGroup | null
  dashboardFilters: TResourceFilter[]
}

const DashboardUserFilter = (props: DashboardUserFilterProps) => {
  const { dashboard, dashboardGroup, dashboardFilters } = props

  const { tryGetAllSavedDashboardFilters } = useSavedDashboardFilterActions()
  const theme = useTheme()
  const [editDashboardUserFilters, setEditDashboardUserFilters] =
    useState<boolean>(false)

  const { fetched: resourceFilterFetched, fetching: resourceFilterFetching } =
    useResourceFilterStatus()
  const resourceFilterData = useResourceFilterData()
  const resourceFilterOptions = useResourceFilterOptions()

  const allowEditDashboardGroup = useAllowEditDashboardGroup(dashboardGroup?.id)
  const shouldFetchSavedDashboardFilters = useShouldFetchSavedDashboardFilters()
  const savedDashboardFilter = useSavedDashboardFilter()
  const dashboardFilter = useDashboardFilter()
  const savedDashboardFilters = useSavedDashboardFilters()
  const periodFilter = usePeriodFilter()
  const isPublic = isPublicDashboard(dashboard)
  const { tryPutDashboardGroupFilterOptions } = useDashboardGroupActions()
  const globalFilterSaveKey = useGlobalFilterSaveKey()
  const { tryGetResourceFilterOptions, tryGetResourceFilterData } =
    useResourceFilterActions()

  const setDashboardFilter = useSetDashboardFilter()
  const setDashboardPeriodFilter = useSetDashboardPeriodFilter()
  const setSavedDashboardFilter = useSetSavedDashboardFilter()

  useEffect(() => {
    if (!isPublic && shouldFetchSavedDashboardFilters) {
      tryGetAllSavedDashboardFilters()
    }
  }, [dashboard, isPublic, shouldFetchSavedDashboardFilters])

  useEffect(() => {
    if (!resourceFilterFetched && !resourceFilterFetching) {
      tryGetResourceFilterOptions()
    }
  }, [])

  useEffect(() => {
    const fetchOptions = dashboardFilters
      .map((dashboardFilter) => ({
        saveKey: `${dashboardFilter.attribute_id}${globalFilterSaveKey}`,
        attributeId: dashboardFilter.attribute_id
      }))
      .filter((item) => !(item.saveKey in resourceFilterData))

    if (fetchOptions.length > 0) {
      tryGetResourceFilterData(fetchOptions)
    }
  }, [globalFilterSaveKey, dashboardFilters])

  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  // dashboard filters for dashboard
  const dashboardFilterOptions = formatResourceFilterOptions(
    dashboardFilters,
    resourceFilterOptions,
    resourceFilterData,
    globalFilterSaveKey
  )

  if (!resourceFilterFetched || !dashboard) {
    return <Loading />
  }

  // success
  return (
    <>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.leavingScreen
          })
        }}
      >
        {/*-- time period --*/}
        <Box sx={{ mb: 2 }}>
          <PeriodSelector
            onSubmit={(period: PeriodFilter | CustomPeriodFilter | null) => {
              setDashboardPeriodFilter(period)

              if (!isPublic) {
                UserEvents.log({
                  action: period
                    ? Action.TIME_PERIOD_FILTERED
                    : Action.TIME_PERIOD_RESETTED,
                  context: period ? Context.FROM_FILTER_MENU : null,
                  dashboard_id: dashboard.id,
                  dashboard_group_id: dashboard.dashboard_group_id
                })
              }
            }}
            periodFilter={periodFilter}
            showActive={true}
          />
        </Box>

        {/*-- filters --*/}
        <DashboardFilterSection
          editable={allowEditDashboardGroup && !isMobile}
          dashboard={dashboard}
          isMobile={isMobile}
          isPublic={isPublic}
          dashboardFilter={dashboardFilter}
          dashboardFilterOptions={dashboardFilterOptions}
          savedDashboardFilters={savedDashboardFilters}
          savedDashboardFilter={savedDashboardFilter}
          setEditDashboardUserFilters={setEditDashboardUserFilters}
          setDashboardFilter={setDashboardFilter}
          setSavedDashboardFilter={setSavedDashboardFilter}
        />
      </Box>

      {/*-- dialogs --*/}
      <EditUserFiltersDialog
        availableDashboardFilterOptions={resourceFilterOptions}
        dashboardFilterOptions={dashboardFilterOptions}
        onClose={() => setEditDashboardUserFilters(false)}
        onSave={(options) => {
          if (!dashboardGroup) {
            return
          }
          if (savedDashboardFilter) {
            // if a saved dashboard filter is selected
            // then validate if it still is valid for dashboard.
            const newOptions = new Map(
              options.map((item) => [item.attribute_id, true])
            )

            const invalidFilter = savedDashboardFilter.conditions.some(
              (condition) => !newOptions.get(condition.attribute_id)
            )

            if (invalidFilter) {
              setSavedDashboardFilter(null) //true
            }
          }

          tryPutDashboardGroupFilterOptions(dashboardGroup.id, options)
        }}
        open={editDashboardUserFilters}
      />
    </>
  )
}

export default DashboardUserFilter
